import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Stack,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  ShieldOutlined as ShieldOutlinedIcon,
  MoreVert as MoreVertIcon,
  ShowChart as ShowChartIcon,
  History as HistoryIcon,
  PlaylistPlay as PlaylistPlayIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { LearningAttentionBadge, SelfImprovedChip, useLearningAttention, useLearningConfigs, useLearningStatus } from './learning/common';
import { learningPath } from '../services/learningApi';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithPublicMode, getPublicModeParams } from '../utils/apiUtils';
import { CodeSnippetsModal } from './CodeSnippetsModal';
import { ReliabilityDialog } from './reliability/ReliabilityDialog';
import {
  DataTable,
  PageHeader,
  ErrorState,
  DeleteConfirmationModal,
  type Column,
  type SortDirection,
} from './common';

interface AIFunction {
  id: string;
  original_uuid?: string;
  name: string;
  display_name?: string;
  description?: string;
  customer_id: string;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  input_types: string[];
  allowed_models?: string[];
  response_format?: {
    type: string;
    object_name?: string;
    is_list?: boolean;
  };
  metadata?: any;
}

export const AIFunctionList: React.FC = () => {
  const { user, permissions, isPublicMode, getEffectiveCustomerTag, hasActiveSubscription, isPlatformAdmin, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const learningStatus = useLearningStatus();
  const learningAttention = useLearningAttention();
  const learningConfigs = useLearningConfigs(learningStatus);

  const renderLearningButton = (row: AIFunction) => {
    if (isPublicMode || row.customer_id === 'public' || !learningStatus) return null;
    if (!learningStatus.enabled) {
      if (!isPlatformAdmin || isImpersonating) return null;
      return (
        <Tooltip title="Self-improvement is off for this account. Click to enable it in Admin.">
          <IconButton size="small" onClick={() => navigate('/dashboard/admin/learning')}>
            <TipsAndUpdatesIcon color="disabled" />
          </IconButton>
        </Tooltip>
      );
    }
    const config = learningConfigs[`function:${row.original_uuid || row.id}`];
    const on = !!config?.enabled;
    return (
      <Tooltip title={on ? "Self-improvement is on: review what it learned from this function's runs" : "Self-improvement: set up learning from this function's runs"}>
        <IconButton size="small" color={on ? 'warning' : 'default'} onClick={() => navigate(learningPath('function', row.id))}>
          <TipsAndUpdatesIcon />
        </IconButton>
      </Tooltip>
    );
  };
  const [functions, setFunctions] = useState<AIFunction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [selectedFunction, setSelectedFunction] = useState<AIFunction | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [functionToDelete, setFunctionToDelete] = useState<AIFunction | null>(null);
  const [orphanedObject, setOrphanedObject] = useState<{ id: string; name: string; description?: string } | undefined>(undefined);
  const [reliabilityFor, setReliabilityFor] = useState<AIFunction | null>(null);
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; row: AIFunction } | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Sorting
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  // Chain function filtering (server-side via exclude_ids)
  const [hideChainFunctions, setHideChainFunctions] = useState(true);
  const [chainFunctionIds, setChainFunctionIds] = useState<Set<string>>(new Set());
  const [chainIdsLoaded, setChainIdsLoaded] = useState(false);

  useEffect(() => {
    const loadChainFunctionIds = async () => {
      try {
        const idToken = await user?.getIdToken();
        const res = await fetch('/api/v2/chains/', {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!res.ok) { setChainIdsLoaded(true); return; }
        const chains = await res.json();
        const ids = new Set<string>();
        for (const chain of chains) {
          if (chain.graph_data?.nodes) {
            for (const node of chain.graph_data.nodes) {
              if (node.functionId && !node.functionId.startsWith('plugin:')) ids.add(node.functionId);
            }
          }
        }
        setChainFunctionIds(ids);
      } catch {
        // non-critical — just don't filter
      } finally {
        setChainIdsLoaded(true);
      }
    };
    if (user) loadChainFunctionIds();
    else setChainIdsLoaded(true);
  }, [user]);

  const loadFunctions = async () => {
    setLoading(true);
    setError(null);
    try {
      const idToken = await user?.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      
      const publicModeParams = getPublicModeParams(isPublicMode, isAdmin);
      
      const params = new URLSearchParams({
        page: String(page + 1),
        page_size: String(rowsPerPage),
        sort_by: sortBy,
        sort_dir: sortDir,
        include_inactive: 'false',
        latest_only: 'true',
        ...publicModeParams,
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (hideChainFunctions && chainFunctionIds.size > 0) {
        params.append('exclude_ids', Array.from(chainFunctionIds).join(','));
      }
      
      const response = await fetchWithPublicMode(
        `/api/v2/ai-functions?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        },
        isPublicMode,
        isAdmin
      );

      if (!response.ok) {
        throw new Error(`Failed to load functions: ${response.statusText}`);
      }

      const data = await response.json();
      setFunctions(data.items || []);
      setTotalCount(data.page_info?.total_items || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load functions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && chainIdsLoaded) {
      loadFunctions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, rowsPerPage, sortBy, sortDir, hideChainFunctions, chainIdsLoaded, chainFunctionIds]);

  const handleSearch = () => {
    setPage(0);
    loadFunctions();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  // Fetch full function details including metadata when opening code examples
  const handleOpenCodeExamples = async (func: AIFunction) => {
    try {
      const idToken = await user?.getIdToken();
      const response = await fetch(`/api/v2/ai-functions/${func.id}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      if (response.ok) {
        const fullFunc = await response.json();
        setSelectedFunction(fullFunc.data || fullFunc);
      } else {
        // Fallback to the list version if fetch fails
        setSelectedFunction(func);
      }
    } catch (error) {
      console.error('Failed to fetch full function details:', error);
      setSelectedFunction(func);
    }
    setCodeModalOpen(true);
  };

  const getEndpoint = (func: AIFunction) => {
    const customerTag = getEffectiveCustomerTag();
    return `/api/v2/functions/${customerTag}/${func.name}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const openDeleteModal = async (func: AIFunction) => {
    setFunctionToDelete(func);
    setOrphanedObject(undefined); // Reset orphaned object
    setDeleteModalOpen(true);
    
    // Fetch full function details to check if it uses a custom object
    try {
      const idToken = await user?.getIdToken();
      // Get the full function details
      const response = await fetch(`/api/v2/ai-functions/${func.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (response.ok) {
        const functionData = await response.json();
        const functionDetails = functionData.data || functionData;
        
        // Check if this function uses a custom object
        if (functionDetails.response_format?.object_id) {
          const objectId = functionDetails.response_format.object_id;
          const objectName = functionDetails.response_format.object_name;
          
          // Check if this object is used by other functions
          const usageResponse = await fetch(`/api/v2/custom-objects/${objectId}/usage`, {
            headers: {
              'Authorization': `Bearer ${idToken}`,
            },
          });
          
          if (usageResponse.ok) {
            const usageData = await usageResponse.json();
            const usage = usageData.data || usageData;
            
            // If only used by this function (or this function + inactive ones), it's orphaned
            const activeFunctionsUsingObject = (usage.functions || []).filter(
              (f: any) => f.id !== func.id && f.is_active !== false
            );
            
            if (activeFunctionsUsingObject.length === 0) {
              setOrphanedObject({
                id: objectId,
                name: objectName || 'Unknown',
                description: functionDetails.response_format.description,
              });
            }
          }
        }
      }
    } catch (err) {

    }
  };

  const handleDelete = async (deleteOrphaned: boolean = false) => {
    if (!functionToDelete) return;

    const idToken = await user?.getIdToken();
    const response = await fetchWithPublicMode(`/api/v2/ai-functions/${functionToDelete.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    }, isPublicMode, permissions?.isAdmin || false);

    if (!response.ok) {
      throw new Error(`Failed to deactivate function: ${response.statusText}`);
    }

    // Check if there's an orphaned object
    let data = null;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    }
    
    // If we got orphaned object info and user hasn't decided yet, close modal and reopen with orphan info
    if (data?.object_id && !deleteOrphaned && !orphanedObject) {
      setOrphanedObject({
        id: data.object_id,
        name: data.object_name || 'Unknown',
        description: data.object_description,
      });
      // Don't close modal - it will re-render with orphaned object option
      return;
    }
    
    // Handle orphaned object if user requested to delete it
    if (deleteOrphaned && orphanedObject) {
      try {
        const objResponse = await fetchWithPublicMode(`/api/v2/custom-objects/${orphanedObject.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }, isPublicMode, permissions?.isAdmin || false);
        
        if (!objResponse.ok) {

        }
      } catch (err) {

      }
    }

    // Reset state and reload
    setFunctionToDelete(null);
    setOrphanedObject(undefined);
    await loadFunctions();
  };

  const columns: Column<AIFunction>[] = [
    {
      id: 'name',
      label: 'Function',
      sortable: true,
      width: '370px',
      render: (value, row) => {
        const endpoint = getEndpoint(row);
        const displayName = row.display_name || value;
        const truncatedEndpoint = endpoint.length > 100 ? endpoint.substring(0, 97) + '...' : endpoint;
        
        return (
          <Box sx={{ py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, maxWidth: '320px' }}>
            <Tooltip title={displayName} arrow placement="top" enterDelay={displayName.length > 40 ? 500 : 1000}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.875rem', 
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0
                }}
              >
                {displayName}
              </Typography>
            </Tooltip>
            <LearningAttentionBadge item={learningAttention[`function:${row.original_uuid || row.id}`]} />
            <SelfImprovedChip item={learningAttention[`function:${row.original_uuid || row.id}`]} />
            </Box>
            {row.description && (
              <Tooltip title={row.description} arrow placement="top" enterDelay={500}>
                <Typography 
                  variant="caption" 
                  color="textSecondary" 
                  sx={{ 
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                    mb: 0.25,
                    maxWidth: '320px'
                  }}
                >
                  {row.description}
                </Typography>
              </Tooltip>
            )}
            <Box sx={{ 
              backgroundColor: '#f5f5f5',
              p: 0.5,
              borderRadius: 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              maxWidth: '100%'
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.65rem',
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              >
                POST
              </Typography>
              <Tooltip title={endpoint} arrow>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontFamily: 'monospace',
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '220px'
                  }}
                >
                  {truncatedEndpoint}
                </Typography>
              </Tooltip>
              <Tooltip title="Copy endpoint">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(`POST ${endpoint}`);
                  }}
                  sx={{ padding: '2px' }}
                >
                  <CopyIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'version',
      label: 'Version',
      sortable: true,
      numeric: true,
      width: '80px',
      render: (value, row) => (
        <Tooltip title="Click to edit and view version history">
          <Typography
            variant="body2"
            sx={{ 
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'underline',
              '&:hover': {
                color: 'primary.dark'
              }
            }}
            onClick={() => {
              // Navigate to editor and scroll to version selector
              navigate(`/dashboard/ai-functions?id=${row.id}&scrollTo=version-selector`);
            }}
          >
            {value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'input_types',
      label: 'Input Types',
      sortable: false,
      width: '140px',
      render: (value: string[]) => {
        const visibleTypes = value.slice(0, 2);
        const remainingTypes = value.slice(2);
        
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {visibleTypes.map(type => (
              <Tooltip key={type} title={type} arrow>
                <Chip 
                  label={type} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Tooltip>
            ))}
            {remainingTypes.length > 0 && (
              <Tooltip 
                title={
                  <Box>
                    {remainingTypes.map((type, idx) => (
                      <div key={idx}>{type}</div>
                    ))}
                  </Box>
                } 
                arrow
              >
                <Chip 
                  label={`+${remainingTypes.length}`} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      width: '200px',
      render: (_, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ flexWrap: 'nowrap' }}>
          <Tooltip title="Test function">
            <IconButton
              size="small"
              onClick={() => navigate(`/dashboard/ai-functions?id=${row.id}&test=true`)}
              color="primary"
            >
              <PlayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={hasActiveSubscription ? "View code examples" : "Requires subscription"}>
            <span>
              <IconButton
                size="small"
                onClick={() => handleOpenCodeExamples(row)}
                color="secondary"
                disabled={!hasActiveSubscription}
              >
                <CodeIcon />
              </IconButton>
            </span>
          </Tooltip>
          {(row.customer_id !== 'public' || (isPublicMode && permissions?.isAdmin)) && (
            <>
              <Tooltip title="Edit function">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/dashboard/ai-functions?id=${row.id}`)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {renderLearningButton(row)}
              <Tooltip title="More: improve with AI, reliability, deactivate">
                <IconButton size="small" onClick={(e) => setRowMenu({ anchor: e.currentTarget, row })}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      ),
    },
    {
      id: 'response_format',
      label: 'Output Type',
      sortable: false,
      width: '140px',
      render: (value: any, row: AIFunction) => {
        // Check response_format for object info
        const responseType = value?.type || 'text';
        const isListResponse = value?.is_list || value?.is_list_response || false;
        const objectName = value?.object_name;
        const objectId = value?.object_id;
        
        let label = 'text';
        if (responseType === 'array' || isListResponse) {
          label = `List<${objectName || 'Object'}>`;
        } else if (responseType === 'object' || objectId) {
          label = objectName || 'Object';
        } else {
          label = responseType;
        }
        
        if (objectId && (responseType === 'object' || responseType === 'array' || isListResponse)) {
          return (
            <Tooltip title="View object details">
              <Chip 
                label={label}
                size="small" 
                color="primary"
                variant={isListResponse ? 'filled' : 'outlined'}
                onClick={() => navigate(`/dashboard/objects?edit=${objectId}`)}
                component="a"
                clickable
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          );
        }
        
        return (
          <Chip 
            label={label}
            size="small" 
            color={responseType === 'text' ? 'default' : 'primary'}
            variant={isListResponse ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      id: 'allowed_models',
      label: 'Models',
      sortable: false,
      width: '160px',
      render: (value: string[] | undefined) => {
        const models = value || ['gpt-4o-mini'];
        const visibleModels = models.slice(0, 2);
        const remainingModels = models.slice(2);
        
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {visibleModels.map(model => (
              <Tooltip key={model} title={model} arrow>
                <Chip 
                  label={model} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Tooltip>
            ))}
            {remainingModels.length > 0 && (
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                      Additional Models:
                    </Typography>
                    {remainingModels.map((model, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
                        {model}
                      </Typography>
                    ))}
                  </Box>
                } 
                arrow
              >
                <Chip 
                  label={`+${remainingModels.length}`} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', cursor: 'help' }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      id: 'created_at',
      label: 'Created',
      sortable: true,
      width: '85px',
      render: (value) => (
        <Tooltip title={value ? new Date(value).toLocaleString() : '-'} arrow>
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            {value ? new Date(value).toLocaleDateString() : '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'updated_at',
      label: 'Updated',
      sortable: true,
      width: '85px',
      render: (value) => (
        <Tooltip title={value ? new Date(value).toLocaleString() : '-'} arrow>
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            {value ? new Date(value).toLocaleDateString() : '-'}
          </Typography>
        </Tooltip>
      ),
    },
  ];


  if (error && !loading) {
    return (
      <Box>
        <PageHeader
          title="AI Functions"
          subtitle="Manage your AI-powered functions"
          actions={[
            { 
              label: 'Create New Function', 
              onClick: () => navigate('/dashboard/ai-functions'),
              variant: 'contained',
            },
            {
              label: 'Generate OpenAPI Spec',
              onClick: async () => {
                try {
                  const idToken = await user?.getIdToken();
                  const response = await fetch('/api/v2/openapi/spec', {
                    headers: {
                      'Authorization': `Bearer ${idToken}`,
                    },
                  });
                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'openapi-spec.json';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  } else {

                  }
                } catch (error) {

                }
              },
              variant: 'outlined',
            },
          ]}
        />
        <ErrorState error={error} onRetry={loadFunctions} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="AI Functions"
        subtitle="Manage your AI-powered functions"
        actions={[
          { 
            label: 'Create New Function', 
            onClick: () => navigate('/dashboard/ai-functions'),
            variant: 'contained',
          },
          {
            label: 'Generate OpenAPI Spec',
            onClick: async () => {
              if (!hasActiveSubscription) {
                return;
              }
              try {
                const idToken = await user?.getIdToken();
                const response = await fetch('/api/v2/openapi/spec', {
                  headers: {
                    'Authorization': `Bearer ${idToken}`,
                  },
                });
                if (response.ok) {
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'openapi-spec.json';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                } else {

                }
              } catch (error) {

              }
            },
            variant: 'outlined',
            disabled: !hasActiveSubscription,
          },
          {
            label: 'Clean Orphans',
            onClick: async () => {
              try {
                const idToken = await user?.getIdToken();
                // Dry run first to show what would be cleaned
                const preview = await fetch('/api/v2/ai-functions/cleanup-orphans?dry_run=true', {
                  method: 'POST', headers: { Authorization: `Bearer ${idToken}` },
                });
                if (!preview.ok) return;
                const previewData = await preview.json();
                if (previewData.total_functions === 0 && previewData.total_objects === 0) {
                  setError('Nothing to clean up — no orphaned functions or objects found.');
                  return;
                }
                const items = [
                  ...previewData.orphaned_functions.map((f: any) => `Function: ${f.display_name || f.name}`),
                  ...previewData.orphaned_objects.map((o: any) => `Object: ${o.display_name || o.name}`),
                ];
                const msg = `Found ${previewData.total_functions} unused function(s) and ${previewData.total_objects} unused object(s):\n\n${items.slice(0, 20).join('\n')}${items.length > 20 ? `\n...and ${items.length - 20} more` : ''}\n\nDeactivate all?`;
                if (!window.confirm(msg)) return;
                // Execute cleanup
                const res = await fetch('/api/v2/ai-functions/cleanup-orphans?dry_run=false', {
                  method: 'POST', headers: { Authorization: `Bearer ${idToken}` },
                });
                if (res.ok) {
                  setError(null);
                  loadFunctions();
                }
              } catch (e) { console.error('Cleanup failed:', e); }
            },
            variant: 'outlined',
          },
        ]}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        searchPlaceholder="Search functions..."
        showSearch
      />

      {chainFunctionIds.size > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={hideChainFunctions}
                onChange={(e) => { setHideChainFunctions(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Hide functions used in chains ({chainFunctionIds.size} hidden)
              </Typography>
            }
          />
        </Box>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataTable
            columns={columns}
            data={functions}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sortBy={sortBy}
            sortDirection={sortDir}
            onSort={handleSort}
            rowKey="id"
            emptyMessage={
              isPublicMode 
                ? "No public functions found."
                : hideChainFunctions
                  ? "All functions are used in chains. Toggle the filter above to see them."
                  : "You haven't created any functions yet. Create your first AI function to get started."
            }
          />
        </CardContent>
      </Card>

      {selectedFunction && (
        <CodeSnippetsModal
          open={codeModalOpen}
          onClose={() => {
            setCodeModalOpen(false);
            setSelectedFunction(null);
          }}
          functionName={selectedFunction.display_name || selectedFunction.name}
          endpoint={getEndpoint(selectedFunction)}
          inputTypes={selectedFunction.input_types || ['text']}
          allowedModels={selectedFunction.allowed_models || ['gpt-4o-mini']}
          metadata={selectedFunction.metadata}
        />
      )}

      {functionToDelete && (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setFunctionToDelete(null);
            setOrphanedObject(undefined);
          }}
          onConfirm={handleDelete}
          title="Deactivate Function"
          itemName={functionToDelete.display_name || functionToDelete.name}
          itemType="function"
          orphanedObject={orphanedObject}
          isDeactivation={true}
        />
      )}

      <Menu
        anchorEl={rowMenu?.anchor}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {rowMenu && [
          <MenuItem
            key="improve"
            disabled={!hasActiveSubscription}
            onClick={() => {
              navigate(`/dashboard/ai-functions?id=${rowMenu.row.id}&improve=true`);
              setRowMenu(null);
            }}
          >
            <ListItemIcon><AutoAwesomeIcon fontSize="small" color="secondary" /></ListItemIcon>
            <ListItemText primary="Change with AI" secondary={hasActiveSubscription ? undefined : 'Requires subscription'} />
          </MenuItem>,
          <MenuItem
            key="batch"
            disabled={!hasActiveSubscription}
            onClick={() => {
              navigate(`/dashboard/tool-runner/${rowMenu.row.id}`);
              setRowMenu(null);
            }}
          >
            <ListItemIcon><PlaylistPlayIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Run on many files"
              secondary={hasActiveSubscription ? 'Run a batch and download the results' : 'Requires subscription'}
            />
          </MenuItem>,
          <MenuItem
            key="reliability"
            onClick={() => {
              setReliabilityFor(rowMenu.row);
              setRowMenu(null);
            }}
          >
            <ListItemIcon><ShieldOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Reliability" secondary="Time limit, retries, model fallback" />
          </MenuItem>,
          <MenuItem
            key="runs"
            disabled={!hasActiveSubscription}
            onClick={() => {
              navigate(`/dashboard/function-runs/${rowMenu.row.id}`);
              setRowMenu(null);
            }}
          >
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Recent runs"
              secondary={hasActiveSubscription ? 'Inputs, outputs and errors' : 'Requires subscription'}
            />
          </MenuItem>,
          <MenuItem
            key="performance"
            disabled={!hasActiveSubscription}
            onClick={() => {
              navigate(`/dashboard/function-runs/${rowMenu.row.id}?tab=performance`);
              setRowMenu(null);
            }}
          >
            <ListItemIcon><ShowChartIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Performance"
              secondary={hasActiveSubscription ? 'Speed, outcomes, time limit, cost' : 'Requires subscription'}
            />
          </MenuItem>,
          <MenuItem
            key="deactivate"
            onClick={() => {
              openDeleteModal(rowMenu.row);
              setRowMenu(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Deactivate" />
          </MenuItem>,
        ]}
      </Menu>

      {reliabilityFor && (
        <ReliabilityDialog
          open
          onClose={() => setReliabilityFor(null)}
          targetType="function"
          targetId={reliabilityFor.original_uuid || reliabilityFor.id}
          targetName={reliabilityFor.display_name || reliabilityFor.name}
          actAsPublic={isPublicMode && !!permissions?.isAdmin}
        />
      )}
    </Box>
  );
};